import React, { useMemo } from "react";
import { useRouter } from "next/router";
import Map, {
  type ICity as ICityUI,
} from "@business-finland/wif-ui-lib/dist/Map";

import { Viewport } from "../../../constants/app";
import useImageDataHandler from "../../../hooks/useImageDataHandler";
import { formatPageLink } from "../../../util/magnolia";

const MAPBOX_TILE_LAYER = process.env.NEXT_PUBLIC_MAPBOX_TILE_LAYER || "";
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || "";

export interface ICity {
  name: string;
  cityLeadText: string;
  address: string;
  alt: string;
  cityImage: Wif.Mgnl.Image;
  externalLink: string;
  externalLinkText: string;
  secondExternalLink: string;
  secondExternalLinkText: string;
}
export interface ICityCompositeField {
  city: ICity;
}
export interface IMapBoxMagnoliaProps {
  multi: Wif.Mgnl.NodeWithList<Wif.Mgnl.Node<ICityCompositeField>>;
}

export function MapBox({ multi }: IMapBoxMagnoliaProps) {
  const { isPreview } = useRouter();
  const imageDataHandler = useImageDataHandler();

  const cities: ICityUI[] = useMemo(() => {
    let cities: ICityUI[] = [];

    if (multi && multi["@nodes"]?.length) {
      cities = multi["@nodes"].flatMap((node) => {
        const city = multi[node].city;

        // Unpublished city items stay unresolved (plain UUID) in the delivery API
        if (!city || typeof city !== "object" || !city.address) return [];

        const imageData = imageDataHandler(city.cityImage, {
          maxViewport: Viewport.sm,
        });

        return {
          name: city.name,
          cityLeadText: city.cityLeadText,
          address: city.address,
          alt: city.alt,
          imageData: {
            ...imageData,
            imageSrc: imageData.src,
            credits: city.cityImage?.metadata?.rights,
          },
          externalLink: city.externalLink
            ? formatPageLink(city.externalLink, isPreview)
            : null,
          externalLinkText: city.externalLinkText ?? null,
          secondExternalLink: city.secondExternalLink
            ? formatPageLink(city.secondExternalLink, isPreview)
            : null,
          secondExternalLinkText: city.secondExternalLinkText ?? null,
        };
      });
    }

    return cities;
  }, [imageDataHandler, multi, isPreview]);

  return (
    <Map
      cities={cities}
      MAPBOX_TILE_LAYER={MAPBOX_TILE_LAYER}
      MAPBOX_TOKEN={MAPBOX_TOKEN}
    />
  );
}

export default MapBox;
